import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, AlertCircle } from 'lucide-react';
import { formatSimpleDate } from '../utils/dateFormatter';
import { supabase } from '../supabaseClient';
import { useRealtimeTables } from '../hooks/useRealtimeTables';
import { getPriorityTextClass, formatPriorityLabel } from '../utils/priorityColors';

const CalendarView = ({ problems, onTaskClick }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month', 'team'
  const [subtasks, setSubtasks] = useState([]);
  const [dayTableExpanded, setDayTableExpanded] = useState(false);
  const [teamTableExpanded, setTeamTableExpanded] = useState(false);

  const loadSubtasks = async () => {
    try {
      const { data, error } = await supabase
        .from('subtasks')
        .select('*, assignee:users!assigned_to(id, name, email, is_active, inactive_user_name)')
        .order('due_date', { ascending: true });

      if (error) throw error;
      setSubtasks(data || []);
    } catch (error) {
      console.error('Error fetching subtasks:', error);
    }
  };

  const getDateKey = (value) => {
    if (!value) return null;

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch all subtasks
  useEffect(() => {
    loadSubtasks();
  }, []);

  useRealtimeTables({
    channelName: 'calendar-subtasks',
    tables: ['subtasks', 'users'],
    onChange: loadSubtasks
  });

  // Get subtasks for a specific task and date
  const getSubtasksForTaskAndDate = (taskId, dateStr) => {
    return subtasks.filter(st => {
      if (st.problem_id !== taskId || !st.due_date) return false;
      const subtaskDateStr = getDateKey(st.due_date);
      return subtaskDateStr === dateStr;
    });
  };

  // Get today's date at midnight for comparison
  const getTodayMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Get tasks with deadlines (excluding Done tasks and subtasks from flattened list)
  const tasksWithDeadlines = useMemo(() => {
    return problems.filter(p => p.due_date && p.status !== 'Done' && p.type === 'problem');
  }, [problems]);

  // Get subtasks for the selected date with their parent task info
  const subtasksForSelectedDate = useMemo(() => {
    const selectedDateStr = getDateKey(selectedDate);

    return subtasks
      .filter(st => {
        if (!st.due_date) return false;
        const subtaskDateStr = getDateKey(st.due_date);
        return subtaskDateStr === selectedDateStr;
      })
      .map(st => {
        const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
        return {
          ...st,
          parentTask: parentTask || null,
          assigned_user: st.assignee || parentTask?.assigned_user || null
        };
      })
      .filter(st => st.parentTask);
  }, [subtasks, problems, selectedDate]);

  // Get main tasks for selected date that don't have any subtasks
  const mainTasksWithoutSubtasks = useMemo(() => {
    const selectedDateStr = getDateKey(selectedDate);

    return tasksWithDeadlines.filter(task => {
      const taskDateStr = getDateKey(task.due_date);
      if (taskDateStr !== selectedDateStr) return false;
      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      return !hasSubtasks;
    });
  }, [tasksWithDeadlines, subtasks, selectedDate]);

  // Calculate countdown
  const getCountdown = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    due.setHours(18, 30, 0, 0);
    const diff = due - now;

    if (diff < 0) {
      const hours = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
      const days = Math.floor(hours / 24);
      if (days > 0) return `${days} day${days > 1 ? 's' : ''} overdue`;
      return `${hours} h overdue`;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} h remaining`;
    return `${minutes} min remaining`;
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const due = new Date(dueDate);
    due.setHours(23, 59, 59, 999);
    return due < today;
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getWeekDays = () => {
    const start = new Date(selectedDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getMonthGrid = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    return days;
  };

  const getSubtasksForDate = (date) => {
    const dateKey = getDateKey(date);

    return subtasks
      .filter(st => {
        if (!st.due_date) return false;
        return getDateKey(st.due_date) === dateKey;
      })
      .map(st => {
        const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
        return {
          ...st,
          parentTask: parentTask || null,
          assigned_user: st.assignee || parentTask?.assigned_user || null
        };
      })
      .filter(st => st.parentTask);
  };

  const getMainTasksWithoutSubtasksForDate = (date) => {
    const dateKey = getDateKey(date);

    return tasksWithDeadlines.filter(task => {
      if (getDateKey(task.due_date) !== dateKey) return false;

      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      return !hasSubtasks;
    });
  };

  const getPriorityStyle = (priority) => {
    return getPriorityTextClass(priority);
  };

  const calendarItems = useMemo(() => {
    const items = [];
    subtasks.forEach(st => {
      const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
      if (!parentTask || parentTask.status === 'Done' || !st.due_date) return;
      items.push({
        id: `sub-${st.id}`,
        title: st.title,
        domain: parentTask.domain,
        status: parentTask.status,
        priority: parentTask.priority,
        due_date: st.due_date,
        assigned_user: st.assignee || parentTask.assigned_user,
        isSubtask: true,
        originalTask: parentTask,
      });
    });

    tasksWithDeadlines.forEach(task => {
      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      if (!hasSubtasks) {
        items.push({
          id: `task-${task.id}`,
          title: task.title,
          domain: task.domain,
          status: task.status,
          priority: task.priority,
          due_date: task.due_date,
          assigned_user: task.assigned_user,
          isSubtask: false,
          originalTask: task,
        });
      }
    });
    return items;
  }, [subtasks, tasksWithDeadlines, problems]);

  return (
    <div className="space-y-6">
      {/* View Mode Tabs */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
        <div className="flex gap-2 bg-white/5 p-1 rounded-lg overflow-x-auto max-w-full no-scrollbar">
          {['day', 'week', 'month', 'team'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md font-medium capitalize transition-colors text-sm whitespace-nowrap ${viewMode === mode
                ? 'bg-white text-black shadow-lg shadow-white/10'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between w-full md:w-auto gap-2 md:gap-3 bg-white/5 p-1 rounded-xl md:bg-transparent md:p-0">
          <button
            onClick={() => navigateDate(-1)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="text-base md:text-lg font-semibold flex-1 md:flex-initial md:min-w-[200px] text-center truncate px-2">
            {viewMode === 'day' && selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            {viewMode === 'week' && (() => {
              const weekDays = getWeekDays();
              const start = weekDays[0];
              const end = weekDays[6];
              return `${start.getDate()} ${start.toLocaleDateString('en-US', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            })()}
            {viewMode === 'month' && selectedDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            })}
            {viewMode === 'team' && selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <button
            onClick={() => navigateDate(1)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          >
            <ChevronRight size={20} />
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white font-medium"
          >
            Today
          </button>
        </div>
      </div>

      {/* Deadline Today Section */}
      {viewMode === 'day' && (
        <div>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-white" />
            Deadline for {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </h3>
          {subtasksForSelectedDate.length === 0 && mainTasksWithoutSubtasks.length === 0 ? (
            <div className="glass-card p-4 text-sm text-gray-400">
              No due dates or subtasks for today.
            </div>
          ) : (
          <div className="space-y-2">
            {subtasksForSelectedDate.map((subtask) => (
              <div
                key={`subtask-${subtask.id}`}
                onClick={() => onTaskClick(subtask.parentTask)}
                className="glass-card p-3 cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-white text-sm">
                      <span className="text-white/70">@{subtask.parentTask.title}</span>
                      <span className="text-gray-400 mx-2">-</span>
                      <span className={subtask.is_completed ? 'line-through text-gray-500' : ''}>
                        {subtask.title}
                      </span>
                    </h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium ${getPriorityStyle(subtask.parentTask.priority)}`}>
                      {formatPriorityLabel(subtask.parentTask.priority)}
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {formatSimpleDate(subtask.due_date)}
                    </span>
                    <span className={`text-sm font-medium ${isOverdue(subtask.due_date) ? 'text-white underline decoration-gray-500 underline-offset-4' : 'text-gray-300'
                      }`}>
                      ({getCountdown(subtask.due_date)})
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {mainTasksWithoutSubtasks.map((task) => (
              <div
                key={`task-${task.id}`}
                onClick={() => onTaskClick(task)}
                className="glass-card p-3 cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-white text-sm">
                      {task.title}
                    </h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium ${getPriorityStyle(task.priority)}`}>
                      {formatPriorityLabel(task.priority)}
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {formatSimpleDate(task.due_date)}
                    </span>
                    <span className={`text-sm font-medium ${isOverdue(task.due_date) ? 'text-white underline decoration-gray-500 underline-offset-4' : 'text-gray-300'
                      }`}>
                      ({getCountdown(task.due_date)})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="flex flex-col md:grid md:grid-cols-7 gap-4">
          {getWeekDays().map((day, index) => {
            const daySubtasks = getSubtasksForDate(day);
            const dayMainTasks = getMainTasksWithoutSubtasksForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const totalItems = daySubtasks.length + dayMainTasks.length;

            return (
              <div key={index} className={`glass-card p-4 ${isToday ? 'border-2 border-white' : ''}`}>
                <div className="mb-3 flex md:block items-center justify-between gap-4">
                  <div className="flex items-center gap-2 md:block">
                    <div className="text-sm md:text-xs text-gray-400 uppercase font-semibold">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-xl md:text-2xl font-bold ${isToday ? 'text-white' : 'text-white/70'}`}>
                      {day.getDate()}
                    </div>
                  </div>
                  <div className="md:hidden">
                    <span className="text-xs bg-white/10 px-2 py-1 rounded-full text-gray-300">
                      {totalItems} tasks
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {totalItems > 0 ? (
                    <>
                      {daySubtasks.map((subtask) => {
                        const isHold = subtask.parentTask.status?.toLowerCase() === 'hold' || subtask.parentTask.status?.toLowerCase() === 'blocked';
                        return (
                          <div
                            key={`subtask-${subtask.id}`}
                            onClick={() => onTaskClick(subtask.parentTask)}
                            className={`p-2 rounded-lg cursor-pointer transition-colors group mb-1 ${
                              subtask.is_completed ? 'bg-white/10 hover:bg-white/20 text-white line-through opacity-50' :
                              isHold ? 'bg-white/5 hover:bg-white/10 text-gray-400' :
                                'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium truncate flex-1">
                                {subtask.title}
                              </span>
                            </div>
                            <div className="text-[10px] opacity-80 truncate">
                              @{subtask.parentTask.title}
                            </div>
                          </div>
                        );
                      })}

                      {dayMainTasks.map((task) => {
                        const isHold = task.status?.toLowerCase() === 'hold' || task.status?.toLowerCase() === 'blocked';
                        return (
                          <div
                            key={`task-${task.id}`}
                            onClick={() => onTaskClick(task)}
                            className={`p-2 rounded-lg cursor-pointer transition-colors group mb-1 ${
                              task.status === 'Done' ? 'bg-white/10 hover:bg-white/20 text-white line-through opacity-50' :
                              isHold ? 'bg-white/5 hover:bg-white/10 text-gray-400' :
                                'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium truncate flex-1">
                                {task.title}
                              </span>
                            </div>
                            <div className="text-xs opacity-60 capitalize truncate">
                              {task.domain}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-4 md:py-4">No tasks</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month View */}
      {viewMode === 'month' && (
        <div>
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
              <div key={day} className="text-center text-[10px] md:text-xs font-medium text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {getMonthGrid().map((day, index) => {
              const daySubtasks = getSubtasksForDate(day.date);
              const dayMainTasks = getMainTasksWithoutSubtasksForDate(day.date);
              const isToday = day.date.toDateString() === new Date().toDateString();
              const allItems = [...daySubtasks, ...dayMainTasks];

              return (
                <div
                  key={index}
                  onClick={() => setSelectedDate(day.date)}
                  className={`min-h-[60px] md:min-h-[120px] p-1 md:p-2 rounded-lg border transition-all cursor-pointer md:cursor-default relative ${day.isCurrentMonth
                    ? 'glass-card border-white/10'
                    : 'bg-white/5 border-white/5 opacity-30'
                    } ${isToday ? 'border-white ring-1 ring-white/50' : ''} ${selectedDate.toDateString() === day.date.toDateString() ? 'bg-white/10 border-white/50' : ''}`}
                >
                  <div className={`text-sm font-medium mb-2 ${day.isCurrentMonth
                    ? isToday ? 'text-white font-bold' : 'text-white'
                    : 'text-gray-600'
                    }`}>
                    {day.date.getDate()}
                  </div>

                  <div className="md:hidden mt-2 flex flex-wrap gap-1 justify-center">
                    {allItems.length > 0 && allItems.slice(0, 4).map((_, i) => (
                      <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < daySubtasks.length
                        ? subtasksForSelectedDate[i]?.is_completed ? 'bg-white opacity-50' : 'bg-white'
                        : 'bg-white/70'
                        }`} />
                    ))}
                    {allItems.length > 4 && <span className="text-[8px] text-gray-500 leading-none">+</span>}
                  </div>

                  <div className="hidden md:block space-y-1">
                    {daySubtasks.map((subtask) => {
                      const isHold = subtask.parentTask.status?.toLowerCase() === 'hold' || subtask.parentTask.status?.toLowerCase() === 'blocked';
                      return (
                        <div
                          key={`subtask-${subtask.id}`}
                          onClick={(e) => { e.stopPropagation(); onTaskClick(subtask.parentTask); }}
                          className={`p-1 rounded text-xs cursor-pointer transition-colors ${subtask.is_completed ? 'bg-white/10 hover:bg-white/20 text-white line-through opacity-50' :
                            isHold ? 'bg-white/5 hover:bg-white/10 text-gray-400' :
                              'bg-white/20 hover:bg-white/30 text-white'
                            }`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="truncate flex-1">{subtask.title}</span>
                          </div>
                        </div>
                      );
                    })}

                    {dayMainTasks.map((task) => {
                      const isHold = task.status?.toLowerCase() === 'hold' || task.status?.toLowerCase() === 'blocked';
                      return (
                        <div
                          key={`task-${task.id}`}
                          onClick={(e) => { e.stopPropagation(); onTaskClick(task); }}
                          className={`p-1 rounded text-xs cursor-pointer transition-colors ${task.status === 'Done' ? 'bg-white/10 hover:bg-white/20 text-white line-through opacity-50' :
                            isHold ? 'bg-white/5 hover:bg-white/10 text-gray-400' :
                              'bg-white/20 hover:bg-white/30 text-white'
                            }`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="truncate">{task.title}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team View */}
      {viewMode === 'team' && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">
                Team Tasks <span className="text-gray-400 text-sm">({calendarItems.length})</span>
              </h3>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#000000] z-10">
                    <tr className="border-b border-white/10">
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Name</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Title</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Active / Due</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Time Info</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {calendarItems.map((item) => (
                      <tr
                        key={item.id}
                        onClick={() => onTaskClick(item.originalTask)}
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                      >
                        <td className="px-3 py-3 text-sm text-white">
                          {item.assigned_user?.name || 'Unassigned'}
                        </td>
                        <td className="px-3 py-3 text-sm text-white">
                          <div className="flex flex-col">
                            <span>{item.title}</span>
                            <span className="text-[10px] text-white/50">{item.originalTask.title}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <span className={`${isOverdue(item.due_date) ? 'text-white font-bold underline' : 'text-white/80'}`}>
                            {formatSimpleDate(item.due_date)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-400">
                          {getCountdown(item.due_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
